import {TTL_PENDIENTES_MS, type AlmacenLocal} from '@/lib/cola-pendientes'

/** Tarjetas saltadas con `D`, guardadas en el aparato por evaluación. No va al backend a propósito:
 * es un marcador personal de la sesión de revisión, no un estado del dato. */
const PREFIJO = 'kairos.bateria.dejadas.'

export function claveDejadas(businessId: string, evaluacionId: string): string {
  return `${PREFIJO}${businessId}.${evaluacionId}`
}

type Guardado = {guardadoEn: number; refs: string[]}

/** Un `localStorage` bloqueado por política del navegador lanza al tocarlo, no al usarlo. */
export function almacenDelAparato(): AlmacenLocal | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

function leerCrudo(almacen: AlmacenLocal, clave: string): Guardado | null {
  try {
    const texto = almacen.getItem(clave)
    if (!texto) return null
    const dato = JSON.parse(texto) as Record<string, unknown>
    if (typeof dato.guardadoEn !== 'number' || !Array.isArray(dato.refs)) return null
    return {guardadoEn: dato.guardadoEn, refs: dato.refs.filter((ref): ref is string => typeof ref === 'string')}
  } catch {
    return null
  }
}

function escribir(almacen: AlmacenLocal, clave: string, refs: string[], ahora: number): void {
  try {
    if (refs.length === 0) almacen.removeItem(clave)
    else almacen.setItem(clave, JSON.stringify({guardadoEn: ahora, refs} satisfies Guardado))
  } catch {
    // Sin cupo o sin permiso: el marcador sigue vivo en memoria durante esta sesión.
  }
}

/** En el orden en que se saltaron; vencido el plazo se borra. */
export function leerDejadas(almacen: AlmacenLocal, clave: string, ahora: number = Date.now()): string[] {
  const guardado = leerCrudo(almacen, clave)
  if (!guardado) return []
  if (ahora - guardado.guardadoEn > TTL_PENDIENTES_MS) {
    escribir(almacen, clave, [], ahora)
    return []
  }
  return guardado.refs
}

/** Idempotente: el orden es el de la primera vez, que decide cuáles 100 caben en la petición. */
export function dejar(almacen: AlmacenLocal, clave: string, ref: string, ahora: number = Date.now()): void {
  const previas = leerDejadas(almacen, clave, ahora)
  if (previas.includes(ref)) return
  escribir(almacen, clave, [...previas, ref], ahora)
}

export function olvidarDejada(almacen: AlmacenLocal, clave: string, ref: string, ahora: number = Date.now()): void {
  const previas = leerDejadas(almacen, clave, ahora)
  if (!previas.includes(ref)) return
  escribir(
    almacen,
    clave,
    previas.filter((guardada) => guardada !== ref),
    ahora
  )
}

export function contarDejadas(businessId: string, evaluacionId: string, ahora: number = Date.now()): number {
  const almacen = almacenDelAparato()
  return almacen ? leerDejadas(almacen, claveDejadas(businessId, evaluacionId), ahora).length : 0
}
